import React, { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import styled from '@emotion/native';
import { BodyText } from '@/shared/components/StyledText';
import { StylistCalendar } from '@/shared/types/generated';
import { lightGreen } from '@/shared/styles/colors';
import useAvailabilityStore from '@/stores/useAvailabilityStore';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import SectionLayout from '../SectionLayout';
import Dropdown from '@/shared/components/Dropdown';
import { SectionTitle } from '../styledComponents';
import { StepComponentProps } from '@/shared/types/availability';
import useDirty from '@/shared/hooks/useDirty';

const Booking = ({
  isAllCompleted,
  onSaveAvailability,
}: StepComponentProps) => {
  const { isDesktop } = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const {
    bookingCalendarId,
    setBookingCalendarId,
    calendars,
    nylasConnections,
  } = useAvailabilityStore(
    useShallow((state) => ({
      bookingCalendarId: state.bookingCalendarId,
      setBookingCalendarId: state.setBookingCalendarId,
      calendars: state.calendars,
      nylasConnections: state.nylasConnections,
    })),
  );

  const isDirty = useDirty(bookingCalendarId);
  const isValid = Boolean(bookingCalendarId);

  const saveAvailability = async () => {
    if (!onSaveAvailability || !bookingCalendarId) return;

    setLoading(true);
    try {
      await onSaveAvailability();
    } finally {
      setLoading(false);
    }
  };

  const existingCalendars =
    calendars?.filter((calendar): calendar is StylistCalendar =>
      Boolean(calendar?.isOwnedByUser),
    ) || [];

  const calendarProvider = nylasConnections?.[0]?.provider
    ? nylasConnections[0].provider.charAt(0).toUpperCase() +
      nylasConnections[0].provider.slice(1)
    : undefined;

  // Format calendars for dropdown
  const calendarOptions = existingCalendars.map((calendar) => ({
    label: calendarProvider
      ? `${calendar.name} - ${calendarProvider}`
      : calendar.name,
    value: calendar.id,
  }));

  const selectedCalendar = bookingCalendarId
    ? calendars.find((cal) => cal?.id === bookingCalendarId)
    : null;

  // Find the selected calendar name for display
  const selectedCalendarName = selectedCalendar
    ? calendarProvider
      ? `${selectedCalendar.name} - ${calendarProvider}`
      : selectedCalendar.name
    : null;

  const dropdownBaseStyle = {
    width: isDesktop ? 438 : 339,
  };

  return (
    <SectionLayout
      heading="Where should sessions go?"
      description="Choose the calendar you want your styling sessions to show up on. We'll handle the behind-the-scenes."
      buttonText={isAllCompleted ? 'Save Changes' : 'Continue'}
      onButtonPress={saveAvailability}
      isButtonDisabled={loading || !isDirty || !isValid}
      isButtonLoading={loading}
      descriptionStyle={{ maxWidth: isDesktop ? 400 : 335 }}
      titleStyle={{ maxWidth: isDesktop ? '100%' : 242 }}
    >
      {existingCalendars.length > 0 ? (
        <CalendarSection>
          <SectionTitle>Calendars*</SectionTitle>
          <DropdownContainer>
            <Dropdown
              options={calendarOptions}
              selectedOption={selectedCalendarName || 'Select a calendar'}
              onSelect={(option) => setBookingCalendarId(Number(option.value))}
              isVisible={isDropdownVisible}
              setIsVisible={setIsDropdownVisible}
              customMenuContentStyle={dropdownBaseStyle}
              customDropdownButtonStyle={{
                ...dropdownBaseStyle,
                marginBottom: 14,
              }}
              dropdownTextStyle={dropdownTextStyle}
            />
          </DropdownContainer>
        </CalendarSection>
      ) : (
        <NoCalendarsMessage>
          No calendars found. Please contact support to disconnect and reconnect
          your calendar.
        </NoCalendarsMessage>
      )}
    </SectionLayout>
  );
};

export default Booking;

const dropdownTextStyle = {
  fontSize: 14,
};

const CalendarSection = styled.View({
  width: '100%',
  marginTop: 16,
  maxWidth: 438,
});

const DropdownContainer = styled.View({
  width: '100%',
  marginTop: 8,
});

const NoCalendarsMessage = styled(BodyText)({
  color: lightGreen,
  textAlign: 'center',
  marginTop: 24,
});
